/**
 * Capture modes for Object Capture uploads.
 *
 * "object" (the default) is the aggressive foreground-isolation profile — best for one
 * discrete subject walked all the way around. "scene" trains on full frames for large
 * equipment / partial-arc captures (e.g. a row of tank containers), preserving ground
 * and surroundings instead of cutting them out. "subject" is the equipment-twin profile:
 * scene-style training (so weakly-textured subjects in cluttered yards still solve),
 * then the model is cut down to the orbited subject plus a ground disc — a clean,
 * fully-orbitable digital twin with the background gone.
 *
 * Pure module (no React/DOM) so the mode policy is unit-testable: the request-body
 * builder, the job/meta mode derivation, and the viewer's default-variant policy all
 * live here.
 */
import type { ObjectMediaInput } from "./apiClient";

export type CaptureMode = "object" | "scene" | "subject";

/** Modes rendered through the SH-capable GaussianSplats3D path (view-dependent color). */
export function isShMode(mode: CaptureMode): boolean {
  return mode === "scene" || mode === "subject";
}

export const CAPTURE_MODES: { id: CaptureMode; label: string; tip: string }[] = [
  {
    id: "object",
    label: "Single object",
    tip: "One full, slow loop around a single subject — keep the whole object in frame.",
  },
  {
    id: "subject",
    label: "Equipment twin",
    tip: "One full, slow loop around large equipment (tanks, containers, machines) — busy backgrounds are fine, they're removed in 3D afterwards.",
  },
  {
    id: "scene",
    label: "Scene / area",
    tip: "A steady sweep across the area with generous overlap between views — everything captured is kept, background included.",
  },
];

/**
 * POST /v1/objects body. `captureMode` is included ONLY for non-default modes — the
 * object-mode body stays byte-identical to what the app has always sent (and the server
 * likewise stores only non-default modes, so default-mode rows never change shape).
 */
export function buildCreateObjectBody(
  media: ObjectMediaInput[],
  label?: string,
  captureMode?: CaptureMode,
): { media: ObjectMediaInput[]; label?: string; captureMode?: "scene" | "subject" } {
  return {
    ...(label ? { label } : {}),
    media,
    ...(captureMode === "scene" || captureMode === "subject"
      ? { captureMode }
      : {}),
  };
}

/**
 * The viewer's mode source of truth: the API job field wins, object-meta.json's
 * captureMode is the fallback (older jobs / direct meta consumers), anything else —
 * absent, junk, wrong type — totalizes to "object" so every pre-existing job renders
 * exactly as before.
 */
export function deriveCaptureMode(
  job?: { captureMode?: string } | null,
  meta?: { captureMode?: unknown } | null,
): CaptureMode {
  if (job?.captureMode === "scene" || job?.captureMode === "subject" || job?.captureMode === "object") {
    return job.captureMode;
  }
  if (meta?.captureMode === "scene" || meta?.captureMode === "subject") return meta.captureMode;
  return "object";
}

/**
 * Which model variant the viewer selects by default. Today this is always the first
 * variant the API listed (model.ply, SH0) — deliberately including scene mode, because
 * the current renderer packs SH0 only, so a model-sh3.ply default would cost a ~4x
 * download for a pixel-identical render. When an SH-capable renderer lands, pass
 * shCapable=true and scene/subject jobs default to the full-SH variant.
 */
export function defaultVariantId(
  mode: CaptureMode,
  models: { id: string }[],
  shCapable = false,
): string | null {
  if (models.length === 0) return null;
  if (isShMode(mode) && shCapable) {
    const sh3 = models.find((m) => m.id === "model-sh3.ply");
    if (sh3) return sh3.id;
  }
  return models[0].id;
}
